// Various network calls reside in here

import { PayTraceSDK, TransactionHistorySearch as TransactionHistorySearchReq } from '@paytrace/pt-sdk'

import { doSignOut, isSessionExpired } from '@paytrace/pt-skel/Component/Helper'
import Persistence from '@paytrace/pt-skel/Context/Persistence'
import PubSub from '@paytrace/pt-skel/Event/PubSub'
import { TokenInspector } from '@paytrace/pt-skel/Helper/TokenInspector'
import moment from 'moment'
import Event from '../../Event'
import { NETWORK_ERROR_BANNER_MESSAGE } from '../../Helper/EnhancedData/Constants'
import { encodeUrlQueryParams } from '../../Helper/Url'
import { EnhancedDataRequest, EnhancedDataTransactionsRequest } from '../../Type/EnhancedData'

import * as T from '../../types'
import { RecurringFormData } from 'Component/Recurring/SearchPayments'

// NOTE: Flip this to 'false' if you want to more easily test back
// end server responses, otherwise this client side evaluation will
// prevent you from seeing/reaching most of them.
// TODO: Should we make this another config/query string
// type setting like AUTH_PROVIDER/auth-provider?
const sdk = new PayTraceSDK({
  clientValidations: true,
  extraHeaders: {
    'X-Entry-Method': 'VT',
  },
})
const bffDomain: string = process.env.BFF_DOMAIN || 'http://localhost:10000'

sdk.setDomain(bffDomain)

// Re-load a previously persisted access-token
// Using the TokenInspector ensures it is compatible with pre-enc and post-enc forms
const accessToken = Persistence.getItem('access-token')
const inspector = TokenInspector.fromJwt(accessToken)
const payload = inspector.getParsed()

sdk.setToken(payload.access_token)
sdk.setRefreshToken(Persistence.getItem('refresh-token'))

// TODO: Right now this is subject to a timing issue if somehow
// the request didn't complete before a user triggers a client
// validation that depends on it - highly unlikely, but we should
// probably add a pre-init routine of things we load before user
// sees the first paint/render on the site itself.
sdk.loadServerSchema().catch(console.error)
sdk.initE2ee().catch(console.error)

interface SchemaErrors {
  [key: string]: string[]
}

function handleSchemaErrors (errors: SchemaErrors) {
  console.debug('Handling schema errors: ', errors)

  Object.keys(errors).forEach((errPath: string) => {
    let eventName = 'VALIDATION_ERROR::' + errPath

    console.debug('Validation error:', { eventName, msg: errors[errPath] })

    if ('VALIDATION_ERROR::input' === eventName) {
      eventName += '/root'
    }

    PubSub.pub(eventName, errors[errPath])
  })
}

interface TransactionSearch {
  startDate?: string
  startTime?: string // Time not currently supported by search API
  endDate?: string
  endTime?: string // Time not currently supported by search API
  orderId: string
  searchText: string
  searchType: string
  paytraceId: number
  limit?: number
  offset?: number
}

interface EmailConfigReq {
  merchant_id: number,
  email_type: string
}

interface BaseCustomerListReq {
  name_filter?: string;
  filter_type?: string
  limit?: number;
  offset?: number;
  include_card_number?: boolean;
  last_activity_days?: number;
  include_associated_merchants?: boolean;
  includeCustomerId?: number
}

interface CustomerListReq extends BaseCustomerListReq {
  merchantId: string
}

interface ProductSearchReq {
  merchantId: number
  keyword: string
  offset?: number
  limit?: number
  filterType?: string
}

// Add the various network listeners
export function init (self: any) {
  PubSub.sub(self, Event.LOGIN_REQUEST, (credentials: { username: string, password: string }) => {
    console.info('Logging in: ', credentials)
    PubSub.pub(Event.SOMETHING_LOADING, { name: 'LOGIN' })

    // This is horribly disjoined - LOGIN_RESPONSE gets picked up by base/LoginForm
    // which is what runs the parsing/relaying of login info to the form user, and
    // also chains off the SET_REFRESH_TOKEN and SET_ACCESS_TOKEN calls - a true pain
    // to update in both spots if needing to switch things around (although we could
    // add additional subs to the event I suppose).
    sdk.login(credentials)
      .then(json => PubSub.pub(Event.LOGIN_RESPONSE, json))
      // FIXME: Requiring re-nesting the object is ugly, but LoginForm is part
      // of the base template stuff - we should add a more clean/general way to provide
      // error feedback on widgets without expecting them to parse, but just receive the data.
      // This will likely be the PubSub SDK clientValidations integration->mapping to form.
      .catch(err => {
        // Some responses may not have a res.message, coalesce them so they do.
        // Otherwise we may not be providing any useful feedback at all on network
        // outages etc.
        if (!err.message) {
          if (err.data) {
            err.message = err.data
          } else {
            err.message = JSON.stringify(err)
          }
        }

        if (err.message.includes('ACL')) {
          PubSub.pub(Event.NETWORK_ERROR, err)
        } else {
          PubSub.pub(Event.LOGIN_RESPONSE, ({ data: { detail: err.message }, status: 'error' }))
        }
      })
      .finally(() => {
        PubSub.pub(Event.DONE_LOADING, { name: 'LOGIN' })
      })
  })

  PubSub.sub(self, Event.NETWORK_ERROR, (err) => {
    // If session is expired when attempting any network request, do logout
    // TODO: Potentially remove left side, or change to check status_code in 4xx range
    if (err.message && err.message.includes('ACL') && isSessionExpired()) {
      PubSub.pub(Event.LOGOUT, {})
    }

    // This indicates the key changed and stashed session/tokens will not work
    if (err.toString().includes('InvalidJWSSignature')) {
      PubSub.pub(Event.LOGOUT, {})
    }
  })

  PubSub.sub(self, Event.LOGOUT, () => {
    doSignOut()
  })

  // FIXME: Customer request is tied to default id, not active id - need to fix in BFF + pt-sdk
  PubSub.sub(
    self,
    Event.GET_CUSTOMER_LIST_REQUEST,
    (args: CustomerListReq) => {
      console.debug('Fetching customer list: ')
      PubSub.pub(Event.SOMETHING_LOADING, { name: 'CUSTOMER_LIST' })

      sdk.getCustomersByMerchantId(
        args.merchantId,
        args.name_filter || '',
        args.offset,
        args.limit,
        args.include_card_number,
        args.last_activity_days,
        args.include_associated_merchants,
        args.filter_type,
      )
        .then(customerList => {
          // since customer list response is paginated, we may need to do an extra lookup
          // if a specific customer is needed in the list
          if (
            args.includeCustomerId &&
            !customerList.filter((x: T.Customer) => parseInt(x.customer_id) === args.includeCustomerId).length
          ) {
            sdk.getCustomer(args.includeCustomerId, 1)
              .then(singlecust => {
                PubSub.pub(Event.GET_CUSTOMER_LIST_RESPONSE, [...customerList, singlecust])
              })
              .catch((err: any) => {
                PubSub.pub(Event.NETWORK_ERROR, new Error('Failed to load customer list: ' + err.message))
              })
          }
          PubSub.pub(Event.GET_CUSTOMER_LIST_RESPONSE, customerList)
        })
        .catch((err: any) => {
          PubSub.pub(Event.NETWORK_ERROR, new Error('Failed to load customer list: ' + err.message))
        })
        .finally(() => PubSub.pub(Event.DONE_LOADING, { name: 'CUSTOMER_LIST' }))
    })

  PubSub.sub(
    self,
    Event.GET_CUSTOMERS_FROM_TOKEN_REQUEST,
    (args: BaseCustomerListReq) => {
      console.debug('Fetching customer list from token')

      PubSub.pub(Event.SOMETHING_LOADING, { name: 'CUSTOMER_FROM_TOKEN_REQUEST' })

      sdk.getCustomers(
        args.name_filter,
        args.offset,
        args.limit,
        args.include_card_number,
        args.last_activity_days,
        args.include_associated_merchants,
        args.filter_type
      )
        .then(customerList => {
          if (
            args.includeCustomerId &&
            !customerList.filter((x: T.Customer) => parseInt(x.customer_id) === args.includeCustomerId).length
          ) {
            sdk.getCustomer(args.includeCustomerId, 1)
              .then(singlecust => {
                PubSub.pub(Event.GET_CUSTOMER_LIST_RESPONSE, [...customerList, singlecust])
              })
              .catch((err: any) => {
                PubSub.pub(Event.NETWORK_ERROR, new Error('Failed to load customer list from token: ' + err.message))
              })
          }
          PubSub.pub(Event.GET_CUSTOMER_LIST_RESPONSE, customerList)
        })
        .catch((err: any) => {
          PubSub.pub(Event.NETWORK_ERROR, new Error('Failed to load customer list from token: ' + err.message))
        })
        .finally(() => {
          PubSub.pub(Event.DONE_LOADING, { name: 'CUSTOMER_FROM_TOKEN_REQUEST' })
        })
    }
  )

  PubSub.sub(
    self,
    Event.GET_USERS_BY_ACCT_REQUEST, (data: {
      name_filter: string,
      filter_type: string,
      include_associated_merchants: boolean,
      instanceId: string,
      offset?: number,
      limit?: number,
    }) => {
      PubSub.pub(Event.SOMETHING_LOADING, { name: 'ACCOUNT_FROM_TOKEN_REQUEST' })

      const params = {
        name: data.name_filter,
        offset: data.offset,
        limit: data.limit,
        include_associated_merchants: true,
      }
      try {
        sdk.findUsers(params).then(users => {
          PubSub.pub(Event.GET_USERS_BY_ACCT_RESPONSE, {
            results: users,
            instanceId: data.instanceId,
          })
        }).catch(err => {
          console.error('Error fetching account from token:', err)
          PubSub.pub(Event.NETWORK_ERROR, err)
        }).finally(() => {
          PubSub.pub(Event.DONE_LOADING, { name: 'ACCOUNT_FROM_TOKEN_REQUEST' })
        })
      } catch (err) {
        console.error('error', err)
      }
    }
  )

  PubSub.sub(
    self,
    Event.SEND_SINGLE_EMAIL_RECEIPT_REQ,
    (mailObject) => {
      PubSub.pub(Event.SOMETHING_LOADING, {
        name: 'SEND_SINGLE_EMAIL_RECEIPT',
      })
      sdk.emailReceipt(mailObject)
        .then(json => PubSub.pub(Event.SEND_SINGLE_EMAIL_RECEIPT_RES, json))
        .catch(err => PubSub.pub(Event.NETWORK_ERROR, err))
        .finally(() => PubSub.pub(Event.DONE_LOADING, { name: 'SEND_SINGLE_EMAIL_RECEIPT' }))
    })

  PubSub.sub(self, Event.GET_CONFIGURED_EMAIL_LIST_REQUEST, (criteria: EmailConfigReq) => {
    console.debug('Fetching email list: ')

    PubSub.pub(Event.SOMETHING_LOADING, { name: 'GET_CONFIGURED_EMAIL' })
    sdk.getMerchantEmailConfig(
      criteria.merchant_id,
      criteria.email_type
    )
      .then(json => PubSub.pub(Event.GET_CONFIGURED_EMAIL_LIST_RESPONSE, json))
      .catch((err: any) => {
        console.error('Failed to load configured email list: ', err)
        PubSub.pub(Event.NETWORK_ERROR, err)
      })
      .finally(() => PubSub.pub(Event.DONE_LOADING, { name: 'GET_CONFIGURED_EMAIL' }))
  })

  PubSub.sub(
    self,
    Event.GET_CUSTOMER_REQUEST,
    async ({ customerId }: { customerId: string }) => {
      console.debug('Fetching customer details: ')
      PubSub.pub(Event.SOMETHING_LOADING, { name: 'GET_CUSTOMER_REQUEST' })

      try {
        const [customer, customerDiscData] = await Promise.all([
          sdk.getCustomer(Number(customerId), 1),
          sdk.getCustomerDiscData(Number(customerId)),
        ])
        PubSub.pub(Event.GET_CUSTOMER_RESPONSE, { customer, customerDiscData })
      } catch (err: any) {
        PubSub.pub(
          Event.NETWORK_ERROR,
          new Error('Failed to load customer: ' + err.message)
        )
      } finally {
        PubSub.pub(Event.DONE_LOADING, { name: 'GET_CUSTOMER_REQUEST' })
      }
    })

  PubSub.sub(self, Event.GET_DISC_DATA_REQUEST, ({ paytraceId }: { paytraceId: string }) => {
    console.debug('Fetching disc data details: ')

    sdk.getDiscData(Number(paytraceId))
      .then(json => PubSub.pub(Event.GET_DISC_DATA_RESPONSE, json))
      .catch((err: any) => {
        console.error('Error fetching discretionary data: ', err)
        PubSub.pub(Event.NETWORK_ERROR, err)
      })
  })

  // Just a front end validator check.
  PubSub.sub(self, Event.RUN_KEYED_SALE_CHECK, (saleDetails) => {
    console.debug('Running keyed sale check', { saleDetails })

    try {
      sdk.validateKeyedSale(saleDetails)
    } catch (err) {
      handleSchemaErrors(err.errors)
    }
  })

  PubSub.sub(self, Event.RUN_SWIPE_SALE_CHECK, (saleDetails) => {
    console.debug('Running swipe sale check', { saleDetails })

    try {
      sdk.validationSwipeTransaction(saleDetails)
    } catch (err) {
      handleSchemaErrors(err.errors)
    }
  })

  PubSub.sub(self, Event.RUN_KEYED_SALE_REQUEST, (saleDetails) => {
    console.debug('Running keyed sale request', { saleDetails })

    PubSub.pub(Event.SOMETHING_LOADING, { name: 'RUN_KEYED_SALE_REQUEST' })

    let authFn = sdk.runKeyedSale

    // We could use the generic sdk method but this is more explicit
    // although I used the generic format for the batch lookups elsewhere.
    if ('only' === saleDetails.auth_type) {
      authFn = sdk.runKeyedAuthOnly
    }

    if ('refund' === saleDetails.auth_type || 'unreferenced_refund' === saleDetails.auth_type) {
      authFn = sdk.runKeyedUnreferencedRefund
    }

    if ('forced' === saleDetails.auth_type) {
      authFn = sdk.runKeyedSaleForced
    }

    authFn(saleDetails)
      .then((json: any) => PubSub.pub(Event.RUN_KEYED_SALE_RESPONSE, json))
      .catch((err: any) => {
        PubSub.pub(Event.DONE_LOADING, { name: 'RUN_KEYED_SALE_REQUEST' })

        if (err.errors && err.errors.detail) {
          // A ResponsableException can trigger this, not tied to any user input
          PubSub.pub(Event.NETWORK_ERROR, err.errors.detail)
        } else if (err.errors) {
          // These can come from backend if out of sync with front end schema I guess
          // This ensures it prints at the top of the screen
          PubSub.pub(Event.NETWORK_ERROR, err.errors)
          // FIXME: This isn't properly rendering on the form when it happens
          // Currently exp year on UI is 4 digits, but API is locking it to 2
          handleSchemaErrors(err.errors)
        } else {
          PubSub.pub(Event.NETWORK_ERROR, err)
        }
      })
      .finally(() => {
        PubSub.pub(Event.DONE_LOADING, { name: 'RUN_KEYED_SALE_REQUEST' })
      })
  })

  PubSub.sub(self, Event.RUN_SWIPE_SALE_REQUEST, (saleDetails) => {
    console.debug('Running swipe sale request', { saleDetails })

    const eventName = 'RUN_SWIPE_SALE_REQUEST'
    PubSub.pub(Event.SOMETHING_LOADING, { name: eventName })

    let authFn = sdk.runSwipeSale

    // We could use the generic sdk method but this is more explicit
    // although I used the generic format for the batch lookups elsewhere.
    if ('only' === saleDetails.auth_type) {
      authFn = sdk.runSwipeAuthOnly
    }

    if ('refund' === saleDetails.auth_type || 'unreferenced_refund' === saleDetails.auth_type) {
      authFn = sdk.runSwipeUnreferencedRefund
    }

    authFn(saleDetails)
      .then((json: any) => PubSub.pub(Event.RUN_SWIPE_SALE_RESPONSE, json))
      .catch((err: any) => {
        PubSub.pub(Event.DONE_LOADING, { name: eventName })

        if (err.errors && err.errors.detail) {
          // A ResponsableException can trigger this, not tied to any user input
          PubSub.pub(Event.NETWORK_ERROR, err.errors.detail)
        } else if (err.errors) {
          // These can come from backend if out of sync with front end schema I guess
          // This ensures it prints at the top of the screen
          PubSub.pub(Event.NETWORK_ERROR, err.errors)
          // FIXME: This isn't properly rendering on the form when it happens
          // Currently exp year on UI is 4 digits, but API is locking it to 2
          handleSchemaErrors(err.errors)
        } else {
          PubSub.pub(Event.NETWORK_ERROR, err)
        }
      })
      .finally(() => {
        PubSub.pub(Event.DONE_LOADING, { name: eventName })
      })
  })

  PubSub.sub(self, Event.REFRESH_TOKEN, () => {
    console.debug('Running refresh token request')

    sdk.loginRefresh()
      .then(json => PubSub.pub(Event.LOGIN_RESPONSE, json))
      .catch(err => {
        PubSub.pub(Event.NETWORK_ERROR, err)
      })
  })

  PubSub.sub(self, Event.SSO_TOKEN, (refreshToken: string, fallbackOnError: boolean = false) => {
    console.debug('Running SSO token request')

    sdk.setToken('') // We just hand off a RT
    sdk.setRefreshToken(refreshToken)

    sdk.loginRefresh()
      .then(json => PubSub.pub(Event.SSO_RESPONSE, json))
      .catch(err => {
        PubSub.pub(Event.NETWORK_ERROR, 'SSO Error: ' + err.message)
        if (fallbackOnError) {
          PubSub.pub(Event.SSO_FALLBACK)
        }
      })
  })

  PubSub.sub(self, Event.GET_CTX_USER, async () => {
    console.debug('Getting user ctx')
    PubSub.pub(Event.SOMETHING_LOADING, { name: 'GET_CTX_USER' })

    try {
      // This is both an optimization, as well as fixes an issue where for
      // some accounts, like admin, merchant may not be in the array.
      const fullMerchantFn = Persistence.getItem('full-merchant-list')
        ? () => Persistence.getItem('full-merchant-list')
        : () => sdk.getMerchants()

      const merchantFn = Persistence.getItem('current-merchant-id')
        ? () => sdk.getMerchant(Persistence.getItem('current-merchant-id'))
        : () => fullMerchantFn()

      let [userRecord, fullMerchantList, merchantList] = await Promise.all([
        sdk.getCtxUser(),
        fullMerchantFn(),
        merchantFn(),
      ])

      if (!Array.isArray(merchantList)) {
        merchantList = [merchantList]
      }

      if (!Persistence.getItem('full-merchant-list')) {
        Persistence.setItem('full-merchant-list', fullMerchantList)
      }

      const flagMerchantId = Persistence.getItem('current-merchant-id') ||
        userRecord.merchant_id
      const rawFeatureFlags = await sdk.getFeatureFlags(flagMerchantId)
      // We want to hoist/elevate the basic flags to the top level
      // but overwrite with the more granular/specific flags (though their
      // names tend to not overlap anyways...)
      const featureFlags = {
        ...rawFeatureFlags.basic_flags,
        ...rawFeatureFlags,
      }
      PubSub.pub(Event.SET_CTX_USER, { userRecord, merchantList, fullMerchantList, featureFlags })
    } catch (err) {
      PubSub.pub(
        Event.NETWORK_ERROR,
        new Error('Failed to load user/merchant context: ' + err.message)
      )
    } finally {
      PubSub.pub(Event.DONE_LOADING, { name: 'GET_CTX_USER' })
    }
  })

  // TODO: Send the date search criteria to the SDK/API (currently not used)
  PubSub.sub(self, Event.SEARCH_TRANSACTIONS_REQUEST, (criteria: TransactionSearch) => {
    console.debug('Fetching transaction list: ')

    if (!['voidable', 'capturable', 'refundable'].includes(criteria.searchType)) {
      console.error('Unknown search type!')
      alert('Unmapped search type - PayTrace developers have been notified!')
      return
    }

    PubSub.pub(Event.SOMETHING_LOADING, { name: 'TRANSACTION_LIST' })

    // there are "proxy" methods specific for search type, but those call this method
    // so just going to keep call this method instead of somehow dynamically calling the explicit ones.
    // Example Curl:
    // GET
    // https://pt-public-api.devqa.paytrace.com/card/transactions/capturable/61100
    //  ?date_from=2026-04-16T00:00:00&date_to=2026-04-16T23:59:59&limit=50&offset=0&search_term=foo&order_id=
    sdk.getTransactionsType(
      criteria.searchType,
      criteria.paytraceId,
      criteria.startDate + 'T' + criteria.startTime + ':00',
      criteria.endDate + 'T' + criteria.endTime + ':59',
      criteria.limit,
      criteria.offset,
      criteria.searchText,
      criteria.orderId,
    )
      .then(json => PubSub.pub(Event.SEARCH_TRANSACTIONS_RESPONSE, json))
      .catch(err => PubSub.pub(Event.NETWORK_ERROR, err))
      .finally(() => PubSub.pub(Event.DONE_LOADING, { name: 'TRANSACTION_LIST' }))
  })

  PubSub.sub(self, Event.BATCH_TRANSACTIONS_OP_REQUEST, (payload: { action: string, merchant_id: number, batch_items: { transaction_id: number }[] }) => {
    PubSub.pub(Event.SOMETHING_LOADING, { name: 'BATCH_TRANSACTIONS_OP' })
    const { action } = payload

    sdk.runBatchOp(action, payload)
      .then((json: any) => PubSub.pub(Event.BATCH_TRANSACTIONS_OP_RESPONSE, json))
      .catch((err: any) => PubSub.pub(Event.BATCH_TRANSACTIONS_OP_RESPONSE, { err }))
      .finally(() => PubSub.pub(Event.DONE_LOADING, { name: 'BATCH_TRANSACTIONS_OP' }))
  })

  PubSub.sub(self, Event.GET_MERCHANT_SURCHARGE_INFO_REQUEST, (merchantId: number) => {
    PubSub.pub(Event.SOMETHING_LOADING, { name: 'GET_MERCHANT_SURCHARGE_INFO' })
    sdk.getMerchantSurchargeInfo(merchantId)
      .then((json: any) => PubSub.pub(Event.GET_MERCHANT_SURCHARGE_INFO_RESPONSE, { data: json, status: 'success' }))
      .catch((err: any) => {
        PubSub.pub(Event.NETWORK_ERROR, new Error('Failed to determine merchant surcharge eligibility: ' + err.message))
        PubSub.pub(Event.GET_MERCHANT_SURCHARGE_INFO_RESPONSE, ({ data: { detail: err.message }, status: 'error' }))
      })
      .finally(() => PubSub.pub(Event.DONE_LOADING, { name: 'GET_MERCHANT_SURCHARGE_INFO' }))
  })

  PubSub.sub(self, Event.GET_TRANSACTION_SURCHARGE_ELIGIBILITY_INFO_REQUEST, (payload: { merchantId: number, billingState: string, integratorId?: string }) => {
    PubSub.pub(Event.SOMETHING_LOADING, { name: 'GET_TRANSACTION_SURCHARGE_ELIGIBILITY_INFO' })
    sdk.getTransactionSurchargeEligibilityInfo(payload.merchantId, payload.billingState, payload.integratorId)
      .then((json: any) => PubSub.pub(Event.GET_TRANSACTION_SURCHARGE_ELIGIBILITY_INFO_RESPONSE, { data: json, status: 'success' }))
      .catch((err: any) => {
        // displaying feedback in the page for this case, so not publishing a network error
        PubSub.pub(Event.GET_TRANSACTION_SURCHARGE_ELIGIBILITY_INFO_RESPONSE, ({ data: { detail: err.message }, status: 'error' }))
      })

      .finally(() => PubSub.pub(Event.DONE_LOADING, { name: 'GET_TRANSACTION_SURCHARGE_ELIGIBILITY_INFO' }))
  })

  PubSub.sub(self, Event.GET_BIN_INFO_REQUEST, (payload: { bin: string, context: string[], billingZipCode?: number }) => {
    PubSub.pub(Event.SOMETHING_LOADING, { name: 'GET_BIN_INFO' })
    sdk.getBinInfo(payload.bin, payload.context, payload.billingZipCode)
      .then((json: any) => PubSub.pub(Event.GET_BIN_INFO_RESPONSE, { data: json, status: 'success' }))
      .catch((err: any) => {
        // displaying feedback in the page for this case, so not publishing a network error
        PubSub.pub(Event.GET_BIN_INFO_RESPONSE, ({ data: { detail: err.message }, status: 'error' }))
      })
      .finally(() => PubSub.pub(Event.DONE_LOADING, { name: 'GET_BIN_INFO' }))
  })

  PubSub.sub(self, Event.RUN_MULTIPLE_TRANX_REQUEST, (payload: any) => {
    PubSub.pub(Event.SOMETHING_LOADING, { name: 'RUN_MULTIPLE_TRANX_REQUEST' })

    sdk.runMultipleTransactions(payload)
      .then((json: any) => PubSub.pub(Event.RUN_MULTIPLE_TRANX_RESPONSE, json))
      .catch((err: any) => {
        PubSub.pub(Event.DONE_LOADING, { name: 'RUN_MULTIPLE_TRANX_REQUEST' })

        if (err.errors && err.errors.detail) {
          // A ResponsableException can trigger this, not tied to any user input
          PubSub.pub(Event.NETWORK_ERROR, err.errors.detail)
        } else if (err.errors) {
          // These can come from backend if out of sync with front end schema I guess
          // This ensures it prints at the top of the screen
          PubSub.pub(Event.NETWORK_ERROR, err.errors)
          // FIXME: This isn't properly rendering on the form when it happens
          // Currently exp year on UI is 4 digits, but API is locking it to 2
          handleSchemaErrors(err.errors)
        } else {
          PubSub.pub(Event.NETWORK_ERROR, err)
        }
      })
      .finally(() => PubSub.pub(Event.DONE_LOADING, { name: 'RUN_MULTIPLE_TRANX_RESPONSE' }))
  })

  PubSub.sub(self, Event.GET_TRANSACTION_BY_ID_REQUEST, (payload: { transactionId: number, merchantId: number }) => {
    PubSub.pub(Event.SOMETHING_LOADING, { name: 'GET_TRANSACTION_BY_ID' })
    sdk.getTransactionById(payload.transactionId, payload.merchantId)
      .then((json: any) => PubSub.pub(Event.GET_TRANSACTION_BY_ID_RESPONSE, json))
      .catch(err => PubSub.pub(Event.NETWORK_ERROR, err))
      .finally(() => PubSub.pub(Event.DONE_LOADING, { name: 'GET_TRANSACTION_BY_ID' }))
  })

  PubSub.sub(self, Event.POST_RECURRING_VIEW_PAYMENT_REQUEST, (payload: {
    formData: RecurringFormData;
    currentPage: number;
    limit: number;
  }) => {
    PubSub.pub(Event.SOMETHING_LOADING, {
      name: 'POST_RECURRING_VIEW_PAYMENT',
    })

    const url = `${bffDomain}/recurrence/export`
    const mapRequestBody = (
      formData: RecurringFormData,
      limit: number,
      currentPage: number
    ) => ({
      frequency: formData.frequency,
      status: formData.status,
      merchant_id: formData.merchant_id,
      customer_id: formData.customerProfile,
      start_date: formData.dateRange.startDate,
      end_date: formData.dateRange.endDate,
      search_term: formData.searchableText,
      limit: limit || 50,
      offset: currentPage,
    })

    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${sdk.getToken()}`,
        'X-Entry-Method': 'VT',
      },
      body: JSON.stringify(
        mapRequestBody(payload.formData, payload.limit, payload.currentPage)
      ),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        return response.json()
      })
      .then((json: any) => {
        PubSub.pub(Event.POST_RECURRING_VIEW_PAYMENT_RESPONSE, json)
      })
      .catch((err) => {
        PubSub.pub(Event.NETWORK_ERROR, err)
      })
      .finally(() => {
        PubSub.pub(Event.DONE_LOADING, {
          name: 'POST_RECURRING_VIEW_PAYMENT',
        })
      })
  }
  )
  PubSub.sub(self, Event.RUN_TRANSACTION_PREVIEW_REQUEST, (payload: { merchant_id: number, amount?: number, swipe?: string, billing_state?: string, card?: any, transaction_id?: number, legacy_transaction_id?: number, customer_id?: number }) => {
    PubSub.pub(Event.SOMETHING_LOADING, { name: 'RUN_TRANSACTION_PREVIEW_REQUEST' })
    sdk.runTransactionPreview(payload)
      .then((json: any) => PubSub.pub(Event.RUN_TRANSACTION_PREVIEW_RESPONSE, { data: json, status: 'success' }))
      .catch((err: any) => {
        const errMessage: string = extractActualErrorMessage(err) || err.message
        PubSub.pub(Event.NETWORK_ERROR, new Error('Failed to process card: ' + errMessage))
      })
      .finally(() => PubSub.pub(Event.DONE_LOADING, { name: 'RUN_TRANSACTION_PREVIEW_REQUEST' }))
  })

  PubSub.sub(self, Event.RUN_RECURRING_PAYMENT_POST_REQUEST, async (payload) => {
    PubSub.pub(Event.SOMETHING_LOADING, {
      name: 'RUN_RECURRING_PAYMENT_POST_REQUEST',
    })
    try {
      const response = await fetch(
        `${bffDomain}/recurrence/create`, // TODO use sdk and shared schema lib
        createRequestObject('POST', payload)
      )

      const actualResponse = await response.json()

      PubSub.pub(
        Event.RUN_RECURRING_PAYMENT_POST_RESPONSE,
        actualResponse
      )
    } catch (err) {
      let errorMessage = 'Failed to post new recurring payment\n'

      if (err && typeof err === 'object' && err.status === 'error' && Array.isArray(err.data)) {
        err.data.forEach((errorItem, index) => {
          errorMessage += `${errorItem.detail}\n`
        })
      } else {
        // Handle cases where the error structure is different
        errorMessage += `Unexpected error format: ${err ? (err.message || err.toString()) : 'Unknown error'}`
      }
      PubSub.pub(
        Event.NETWORK_ERROR,
        new Error(errorMessage)
      )
    } finally {
      PubSub.pub(Event.DONE_LOADING, {
        name: 'RUN_RECURRING_PAYMENT_POST_REQUEST',
      })
    }
  })

  PubSub.sub(self, Event.RUN_RECURRING_PAYMENT_PUT_REQUEST, async (payload) => {
    PubSub.pub(Event.SOMETHING_LOADING, {
      name: 'RUN_RECURRING_PAYMENT_PUT_REQUEST',
    })
    try {
      const response = await fetch(
        `${bffDomain}/recurrence`, // TODO use sdk and shared schema lib
        createRequestObject('PATCH', payload)
      )

      const actualResponse = await response.json()

      PubSub.pub(
        Event.RUN_RECURRING_PAYMENT_PUT_RESPONSE,
        actualResponse
      )
    } catch (err) {
      let errorMessage = 'Failed to update recurring payment\n'

      if (err && typeof err === 'object' && err.status === 'error' && Array.isArray(err.data)) {
        err.data.forEach((errorItem, index) => {
          errorMessage += `${errorItem.detail}\n`
        })
      } else {
        // Handle cases where the error structure is different
        errorMessage += `Unexpected error format: ${err ? (err.message || err.toString()) : 'Unknown error'}`
      }
      PubSub.pub(
        Event.NETWORK_ERROR,
        new Error(errorMessage)
      )
    } finally {
      PubSub.pub(Event.DONE_LOADING, {
        name: 'RUN_RECURRING_PAYMENT_PUT_REQUEST',
      })
    }
  }
  )

  PubSub.sub(self, Event.DELETE_RECURRING_PAYMENT_REQUEST, async (payload) => {
    PubSub.pub(Event.SOMETHING_LOADING, {
      name: 'DELETE_RECURRING_PAYMENT_REQUEST',
    })
    try {
      const response = await fetch(
        `${bffDomain}/recurrence`, // TODO use sdk and shared schema lib
        createRequestObject('DELETE', payload)
      )

      const actualResponse = await response.json()

      PubSub.pub(
        Event.DELETE_RECURRING_PAYMENT_RESPONSE,
        actualResponse
      )
    } catch (err) {
      let errorMessage = 'Failed to delete recurring payment\n'

      if (err && typeof err === 'object' && err.status === 'error' && Array.isArray(err.data)) {
        err.data.forEach((errorItem, index) => {
          errorMessage += `${errorItem.detail}\n`
        })
      } else {
        // Handle cases where the error structure is different
        errorMessage += `Unexpected error format: ${err ? (err.message || err.toString()) : 'Unknown error'}`
      }
      PubSub.pub(
        Event.NETWORK_ERROR,
        new Error(errorMessage)
      )
    } finally {
      PubSub.pub(Event.DONE_LOADING, {
        name: 'DELETE_RECURRING_PAYMENT_REQUEST',
      })
    }
  }
  )

  PubSub.sub(self, Event.GET_TRANSACTION_PREVIEW_INFO_REQUEST, async (payload) => {
    PubSub.pub(Event.SOMETHING_LOADING, {
      name: 'GET_TRANSACTION_PREVIEW_INFO_REQUEST',
    })
    try {
      const response = await fetch(
        `${bffDomain}/transaction/preview`, // TODO use sdk and shared schema lib
        createRequestObject('POST', payload)
      )

      const actualResponse = await response.json()

      PubSub.pub(
        Event.GET_TRANSACTION_PREVIEW_INFO_RESPONSE,
        actualResponse
      )
    } catch (err) {
      let errorMessage = 'Failed to delete recurring payment\n'

      if (err && typeof err === 'object' && err.status === 'error' && Array.isArray(err.data)) {
        err.data.forEach((errorItem, index) => {
          errorMessage += `${errorItem.detail}\n`
        })
      } else {
        // Handle cases where the error structure is different
        errorMessage += `Unexpected error format: ${err ? (err.message || err.toString()) : 'Unknown error'}`
      }
      PubSub.pub(
        Event.NETWORK_ERROR,
        new Error(errorMessage)
      )
    } finally {
      PubSub.pub(Event.DONE_LOADING, {
        name: 'GET_TRANSACTION_PREVIEW_INFO_REQUEST',
      })
    }
  }
  )

  PubSub.sub(self, Event.GET_ACH_MERCHANT_SETTINGS_REQUEST,
    async ({ merchantId }: { merchantId: string }) => {
      PubSub.pub(Event.SOMETHING_LOADING, {
        name: 'GET_ACH_MERCHANT_SETTINGS_REQUEST',
      })
      try {
        const response = await fetch(
          `${bffDomain}/merchant/ach/${merchantId}`, // TODO use sdk and shared schema lib
          createRequestObject('GET')
        )

        const actualResponse = await response.json()
        PubSub.pub(Event.GET_ACH_MERCHANT_SETTINGS_RESPONSE, actualResponse.data)
      } catch (err) {
        PubSub.pub(
          Event.NETWORK_ERROR,
          new Error('Failed to fetch merchant settings')
        )
      } finally {
        PubSub.pub(Event.DONE_LOADING, {
          name: 'GET_ACH_MERCHANT_SETTINGS_REQUEST',
        })
      }
    }
  )

  PubSub.sub(self, Event.GET_RECURRING_CHANGE_LOG_REQUEST, async (payload: { merchant_id: number, recurring_id: number, limit: number, offset: number }) => {
    const request_name = 'GET_RECURRING_CHANGE_LOG_REQUEST'
    PubSub.pub(Event.SOMETHING_LOADING, { name: request_name })

    try {
      const url = new URL(`${bffDomain}/recurrence/change-log`)
      const body = { merchant_id: payload.merchant_id, recurring_id: payload.recurring_id, limit: payload.limit, offset: payload.offset }

      const response = await fetch(
        url.toString(),
        createRequestObject('POST', body)
      )

      const actualResponse = await response.json()
      PubSub.pub(Event.GET_RECURRING_CHANGE_LOG_RESPONSE, actualResponse.data)
    } catch (err) {
      PubSub.pub(
        Event.NETWORK_ERROR,
        new Error('Failed to fetch recurring change log Data')
      )
    } finally {
      PubSub.pub(Event.DONE_LOADING, {
        request_name,
      })
    }
  })

  PubSub.sub(self, Event.GET_RECURRENCE_LIST_REQUEST,
    async (payload) => {
      PubSub.pub(Event.SOMETHING_LOADING, {
        name: 'GET_RECURRENCE_LIST_REQUEST',
      })
      try {
        const response = await fetch(
          `${bffDomain}/recurrence/export`, // TODO use sdk and shared schema lib
          createRequestObject('POST', payload)
        )

        const actualResponse = await response.json()
        PubSub.pub(Event.GET_RECURRENCE_LIST_RESPONSE, actualResponse.data)
      } catch (err) {
        PubSub.pub(
          Event.NETWORK_ERROR,
          new Error('Failed to fetch merchant settings')
        )
      } finally {
        PubSub.pub(Event.DONE_LOADING, {
          name: 'GET_RECURRENCE_LIST_REQUEST',
        })
      }
    }
  )

  PubSub.sub(self, Event.GET_RECURRING_PAYMENT_REQUEST, (payload: {
    formData: RecurringFormData;
    currentPage: number;
    limit: number;
  }) => {
    PubSub.pub(Event.SOMETHING_LOADING, {
      name: 'POST_RECURRING_VIEW_PAYMENT',
    })

    const url = `${bffDomain}/recurrence/export`
    const mapRequestBody = (
      formData: RecurringFormData,
      limit: number,
      currentPage: number
    ) => ({
      frequency: formData.frequency || '',
      status: formData.status || '',
      merchant_id: formData.merchant_id || null,
      customer_id: formData.customerProfile || null,
      start_date: formData.dateRange.startDate || '',
      end_date: formData.dateRange.endDate || '',
      search_term: formData.searchableText || '',
      limit: limit || 50,
      offset: currentPage,
    })

    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${sdk.getToken()}`,
        'X-Entry-Method': 'VT',
      },
      body: JSON.stringify(
        mapRequestBody(payload.formData, payload.limit, payload.currentPage)
      ),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        return response.json()
      })
      .then((json: any) => {
        PubSub.pub(Event.GET_RECURRING_PAYMENT_RESPONSE, json)
      })
      .catch((err) => {
        PubSub.pub(Event.NETWORK_ERROR, err)
      })
      .finally(() => {
        PubSub.pub(Event.DONE_LOADING, {
          name: 'POST_RECURRING_VIEW_PAYMENT',
        })
      })
  })

  PubSub.sub(self, Event.POST_RECURRENCE_CONSENT_FORM_REQUEST, (payload: any) => {
    if (!payload.email || !payload.merchant_id || !payload.recurring_id) {
      return
    }

    PubSub.pub(Event.SOMETHING_LOADING, {
      name: 'POST_RECURRENCE_CONSENT_FORM_REQUEST',
    })

    const url = `${bffDomain}/v3/recurrence/consent-form`
    fetch(url, {
      method: 'POST',
      headers: {
        accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${sdk.getToken()}`,
        'X-Entry-Method': 'VT',
      },
      body: JSON.stringify(payload),
    })
      .then((response: any) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        return response.json()
      })
      .then((json: any) => {
        PubSub.pub(Event.POST_RECURRENCE_CONSENT_FORM_RESPONSE, json)
      })
      .catch((err) => {
        PubSub.pub(Event.NETWORK_ERROR, err)
      })
      .finally(() => {
        PubSub.pub(Event.DONE_LOADING, {
          name: 'POST_RECURRENCE_CONSENT_FORM_REQUEST',
        })
      })
  })

  PubSub.sub(self, Event.GET_CUSTOMER_ACH_DETAILS_REQUEST, (payload: {customer_id: number}) => {
    if (!payload.customer_id) {
      return
    }

    PubSub.pub(Event.SOMETHING_LOADING, {
      name: 'RECURRENCE_ACH_DETAILS',
    })

    const url = `${bffDomain}/customer/ach/${payload.customer_id}`
    fetch(url, {
      method: 'GET',
      headers: {
        accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${sdk.getToken()}`,
        'X-Entry-Method': 'VT',
      },
    })
      .then((response: any) => {
        return response.json()
      })
      .then((json: any) => {
        PubSub.pub(Event.GET_CUSTOMER_ACH_DETAILS_RESPONSE, json)
      })
      .catch((err) => {
        PubSub.pub(Event.NETWORK_ERROR, err)
      })
      .finally(() => {
        PubSub.pub(Event.DONE_LOADING, {
          name: 'RECURRENCE_ACH_DETAILS',
        })
      })
  })

  PubSub.sub(self, Event.GET_TRANSACTION_PREVIEW_REQUEST, (payload: {
      merchant_id: number
      billing_state: string
      customer_id: number
    }) => {
    PubSub.pub(Event.SOMETHING_LOADING, {
      name: 'GET_IS_STATE_SURCHARGE_REQUEST',
    })

    const url = `${bffDomain}/v3/transaction/preview`

    fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${sdk.getToken()}`,
        accept: 'application/json',
        'X-Entry-Method': 'VT',
      },
      body: JSON.stringify(payload),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }
        return response.json()
      }).then((json: any) => {
        PubSub.pub(Event.GET_TRANSACTION_PREVIEW_RESPONSE, json)
      })
      .catch((error) => {
        PubSub.pub(Event.NETWORK_ERROR, error)
      })
      .finally(() => {
        PubSub.pub(Event.DONE_LOADING, {
          name: 'GET_IS_STATE_SURCHARGE_REQUEST',
        })
      })
  })

  PubSub.sub(self, Event.TERMINAL_LIST_REQUEST, ({ merchantId }: { merchantId: string }) => {
    PubSub.pub(Event.SOMETHING_LOADING, { name: 'TERMINAL_LIST_REQUEST' })
    sdk.getPaxTerminalList(merchantId)
      .then((json: any) => PubSub.pub(Event.TERMINAL_LIST_RESPONSE, json))
      .catch((err: any) => {
        const errMessage: string = extractActualErrorMessage(err) || err.message
        PubSub.pub(Event.NETWORK_ERROR, new Error('Failed to get terminal list: ' + errMessage))
      })
      .finally(() => PubSub.pub(Event.DONE_LOADING, { name: 'TERMINAL_LIST_REQUEST' }))
  })

  PubSub.sub(self, Event.RUN_TERMINAL_SALE_REQUEST, (terminalId, authType, saleDetails) => {
    PubSub.pub(Event.SOMETHING_LOADING, { name: 'TERMINAL_SALE_REQUEST' })
    sdk.runAuthByTerminalId(authType, terminalId, saleDetails)
      .then((json: any) => PubSub.pub(Event.RUN_TERMINAL_SALE_RESPONSE, json))
      .catch((err: any) => {
        const errMessage: string = extractActualErrorMessage(err) || err.message
        PubSub.pub(Event.NETWORK_ERROR, new Error('Failed to run terminal sale: ' + errMessage))
        PubSub.pub(Event.RUN_TERMINAL_SALE_RESPONSE, { payload: 'Failed to run terminal sale: ' + errMessage })
      })
      .finally(() => PubSub.pub(Event.DONE_LOADING, { name: 'TERMINAL_SALE_REQUEST' }))
  })

  PubSub.sub(self, Event.TERMINAL_REQUEST_STATUS, async (requestId) => {
    try {
      const [status, responses] = await Promise.all([
        sdk.getTerminalRequestById(requestId),
        sdk.getTerminalResponsesByRequestId(requestId),
      ])
      PubSub.pub(Event.TERMINAL_REQUEST_STATUS_RESPONSE, { status, responses })
    } catch (err) {
      const errMessage: string = extractActualErrorMessage(err) || err.message
      PubSub.pub(
        Event.NETWORK_ERROR,
        new Error('Failed to get terminal request info: ' + errMessage)
      )
    }
  })

  PubSub.sub(self, Event.GET_EMV_REPORT_BY_INTERNAL_IDS, async (internalIds) => {
    try {
      const res = await sdk.getPaxReportSummaryByInternalIds(internalIds)
      PubSub.pub(Event.GET_EMV_REPORT_BY_INTERNAL_IDS_RESPONSE, res)
    } catch (err) {
      const errMessage: string = extractActualErrorMessage(err) || err.message
      PubSub.pub(
        Event.NETWORK_ERROR,
        new Error('Failed to get pax emv void request info: ' + errMessage)
      )
    }
  })

  PubSub.sub(self, Event.SEARCH_TRANSACTION_HISTORY_REQUEST, (searchParams: TransactionHistorySearchReq) => {
    PubSub.pub(Event.SOMETHING_LOADING, { name: 'TRANSACTION_HISTORY_SEARCH' })

    sdk.searchTransactions(
      searchParams
    )
      .then(json => PubSub.pub(Event.SEARCH_TRANSACTION_HISTORY_RESPONSE, json))
      .catch(err => PubSub.pub(Event.NETWORK_ERROR, err))
  })

  PubSub.sub(self, Event.DOWNLOAD_TRANSACTION_HISTORY_REQUEST, async (
    payload: TransactionHistorySearchReq & { is_detail?: boolean }
  ) => {
    PubSub.pub(Event.SOMETHING_LOADING, { name: 'TRANSACTION_HISTORY_SEARH_DOWNLOAD' })

    try {
      const csv_payload = {
        ...payload,
        is_csv: true,
      }
      const timeToken = moment().format('HHmmss')
      // in the legacy app the file naming is using the merchant number i believe.
      // merchant 3 is using 901021. I see it in the full-merchant-list in context as
      // merchant_identification but can i use this list? just using merchant id now and
      // will change it if it is required.
      const filename = `${payload.merchant_id}_${timeToken}_Transactions.csv`
      const file_blob_content = await sdk.downloadSearchTransactions(csv_payload)

      PubSub.pub(Event.DOWNLOAD_TRANSACTION_HISTORY_RESPONSE, {
        filename,
        content: file_blob_content,
      })
    } catch (err) {
      const errMessage: string = extractActualErrorMessage(err) || err.message
      PubSub.pub(
        Event.NETWORK_ERROR,
        new Error('Failed to download search transactions: ' + errMessage)
      )
    } finally {
      PubSub.pub(Event.DONE_LOADING, { name: 'TRANSACTION_HISTORY_SEARCH_DOWNLOAD' })
    }
  })

  PubSub.sub(self, Event.OPEN_NEW_TRANX_SEARCH_TAB, (url) => {
    console.info(`opening new tab`, url)
    const queryParams = encodeUrlQueryParams<TransactionHistorySearchReq>(url)
    window.open(`${url.path}?print=${url.isPrint}&q=${encodeURIComponent(queryParams)}`, '_blank')
  })

  PubSub.sub(self, Event.SAVE_ENHANCED_DATA_REQUEST, (
    transactionId: string | number,
    payload: EnhancedDataRequest
  ) => {
    PubSub.pub(Event.SOMETHING_LOADING, { name: 'SAVE_ENHANCED_DATA_REQUEST' })
    console.debug('transactionId:', transactionId)
    sdk.processEnhancedData(transactionId, payload)
      .then((json: any) => PubSub.pub(Event.SAVE_ENHANCED_DATA_RESPONSE, { data: json, status: 'success' }))
      .catch((err: any) => PubSub.pub(Event.SAVE_ENHANCED_DATA_RESPONSE_ERROR, err))
      .finally(() => PubSub.pub(Event.DONE_LOADING, { name: 'SAVE_ENHANCED_DATA_REQUEST' }))
  })

  PubSub.sub(self, Event.GET_ENHANCED_DATA_TRANSACTIONS_REQUEST, (
    payload: EnhancedDataTransactionsRequest
  ) => {
    PubSub.pub(Event.SOMETHING_LOADING, { name: 'GET_ENHANCED_DATA_TRANSACTIONS_REQUEST' })

    sdk.fetchEnhancedDataTransactions(payload)
      .then((json: any) => PubSub.pub(Event.GET_ENHANCED_DATA_TRANSACTIONS_RESPONSE, { data: json, status: 'success' }))
      .catch((err: any) => PubSub.pub(Event.GET_ENHANCED_DATA_TRANSACTIONS_RESPONSE_ERROR, err))
      .finally(() => PubSub.pub(Event.DONE_LOADING, { name: 'GET_ENHANCED_DATA_TRANSACTIONS_REQUEST' }))
  })

  PubSub.sub(
    self,
    Event.GET_ED_MERCHANT_TAX_ID_SETTING_REQUEST, ({ merchantId }: { merchantId: number }) => {
      PubSub.pub(Event.SOMETHING_LOADING, {
        name: 'GET_ED_MERCHANT_TAX_ID_SETTING_REQUEST',
      })
      sdk.getMerchantEnhancedData(Number(merchantId))
        .then(json => PubSub.pub(Event.GET_ED_MERCHANT_TAX_ID_SETTING_RESPONSE, json))
        .catch(err => PubSub.pub(Event.NETWORK_ERROR, err))
        .finally(() => PubSub.pub(Event.DONE_LOADING, { name: 'GET_ED_MERCHANT_TAX_ID_SETTING_REQUEST' }))
    })

  PubSub.sub(
    self,
    Event.UPDATE_ED_MERCHANT_TAX_ID_SETTING_REQUEST,
    ({ merchantId, payload }: { merchantId: number, payload: any }) => {
      PubSub.pub(Event.SOMETHING_LOADING, {
        name: 'UPDATE_ED_MERCHANT_TAX_ID_SETTING_REQUEST',
      })
      sdk.updateMerchantEnhancedData(merchantId, payload)
        .then(json => PubSub.pub(Event.UPDATE_ED_MERCHANT_TAX_ID_SETTING_RESPONSE, json))
        .catch(err => PubSub.pub(Event.NETWORK_ERROR, err))
        .finally(() => PubSub.pub(Event.DONE_LOADING, { name: 'UPDATE_ED_MERCHANT_TAX_ID_SETTING_REQUEST' }))
    }
  )

  PubSub.sub(self, Event.GET_TRANSACTION_DETAILS_FOR_MANUAL_FILL_REQUEST, (payload: {transactionId: number}) => {
    PubSub.pub(Event.SOMETHING_LOADING, { name: 'GET_TRANSACTION_DETAILS_FOR_ENHANCED_DATA_REQUEST' })
    sdk.fetchEnhancedDataTransactionDetails(payload.transactionId)
      .then((json: any) => PubSub.pub(Event.GET_TRANSACTION_DETAILS_FOR_MANUAL_FILL_RESPONSE, json))
      .catch(err => {
        console.debug(err) // logging actual error for debugging
        PubSub.pub(Event.NETWORK_ERROR, NETWORK_ERROR_BANNER_MESSAGE) // showing generic error message in the UI
      })
      .finally(() => PubSub.pub(Event.DONE_LOADING, { name: 'GET_TRANSACTION_DETAILS_FOR_ENHANCED_DATA_REQUEST' }))
  })

  PubSub.sub(self, Event.SEARCH_PRODUCTS_REQUEST, (searchParams: ProductSearchReq) => {
    const { merchantId, offset = 0, limit = 10, keyword, filterType } = searchParams

    sdk.getMerchantEnhancedDataProducts(
      merchantId,
      offset,
      limit,
      keyword, // Maps keyword to the 'filter' param
      filterType
    )
      .then(json => PubSub.pub(Event.SEARCH_PRODUCTS_RESPONSE, json))
      .catch(err => PubSub.pub(Event.NETWORK_ERROR, err))
  })
}

// TODO: this needs to be referenced from the util
const extractActualErrorMessage = (err: any) => {
  const errors: any = err?.errors?.input
  if (errors === undefined) {
    return err.message
  }
  if (Array.isArray(errors) && errors.length > 0) {
    return errors.join()
  }
}

function createRequestObject (method: string, body?: any, entry_feature?: string) {
  const accessToken = Persistence.getItem('access-token')
  const inspector = TokenInspector.fromJwt(accessToken)
  const payload = inspector.getParsed()
  const options = {
    method,
    headers: {
      'Content-Type': 'application/json',
      accept: 'application/json',
      Authorization: 'Bearer ' + payload.access_token,
      'X-Entry-Method': 'VT',
    },
  }
  if (method === 'POST' || method === 'PUT' || method === 'PATCH' || method === 'DELETE') {
    options.body = JSON.stringify(body)
    if (entry_feature) {
      const entry_feature_pair = { 'Entry-Feature': entry_feature }
      options.headers = { ...options.headers, ...entry_feature_pair }
    }
  }
  return options
}

// TODO: Make this work with the poc-sdk
// sdk.getRecurringViewPayment({formData: payload.formData, fromDate: payload.fromDate, toDate: payload.toDate})
//   .then((json: any) => PubSub.pub(Event.POST_RECURRING_VIEW_PAYMENT_RESPONSE, json))
//   .catch(err => PubSub.pub(Event.NETWORK_ERROR, err))
//   .finally(() => PubSub.pub(Event.DONE_LOADING, { name: 'POST_RECURRING_VIEW_PAYMENT' }))
// })

export function deinit (self: any) {
  PubSub.unsub(self)
}
